import { Component, OnInit, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ColDef } from 'ag-grid-community';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

interface ApiResponse {
  region_instance_count: Array<{
    shipper_region: string;
    consignee_region: string;
    instance_count: number;
  }>;
  top_five_customers_per_region: Array<{
    shipper_region: string;
    consignee_region: string;
    customer_contact_uuid: string;
    customer_name: string;
    instance_count: number;
  }>;
  expedited_and_high_priority_counts: Array<{
    lading_uuid: string;
    alternate_reference: string;
    customer_name: string;
    carrier_name: string;
    lading_status: string;
    expedited_count: number;
    high_priority_count: number;
  }>;
  missed_appointment_analysis: Array<{
    lading_uuid: string;
    alternate_reference: string;
    customer_name: string;
    carrier_name: string;
    appointment_start: string;
    appointment_end: string;
    load_start: string;
    load_end: string;
    departure_status: string;
    arrival_status: string;
  }>;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewInit {
  public data: any[] = [];
  public topFiveCustomers: any[] = [];
  public loading = true;
  public errorMessage: string | null = null;
  private instanceCountChart: any;
  private customerChart: any;
  private timeline: any;
  public shipperRegions: string[] = [];
  public consigneeRegions: string[] = [];
  public selectedShipperRegion: string = 'ANY';
  public selectedConsigneeRegion: string = 'ANY';
  public selectedCustomer: string | null = ''; // Define selectedCustomer here
  public selectedCustomerShippers: string[] = []; // Define shippers for selected customer
  public selectedShipper: string | null = ''; // Define selectedShipper here
  public instanceCount: number | string = 'N/A';
  public filteredCustomerData: any[] = []; // Define filteredCustomerData here
  public missedAppointments: any[] = [];
  public filteredAppointments: any[] = []; // Define filteredAppointments here
  public uniqueCustomers: string[] = []; // Define unique customers for the dropdown
  public uniqueTimelineCustomers: string[] = []; // Define unique timeline customers for the dropdown
  public columnDefs: ColDef[] = [
    { field: 'customer_name', headerName: 'Customer Name' },
    { field: 'carrier_name', headerName: 'Carrier Name' },
    { field: 'lading_status', headerName: 'Lading Status' },
    { field: 'expedited_count', headerName: 'Expedited Count' },
    { field: 'high_priority_count', headerName: 'High Priority Count' },
  ];
  public rowData: any[] = [];
  public selectedTimelineCustomer: string = '';
  public selectedTimelineDate: string = '';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.fetchDashboardData();
    this.fetchsmallDashboardData();
    this.initCustomerChart();
  }

  ngAfterViewInit(): void {
    this.initInstanceCountChart();
  }
  fetchDashboardData(): void {
    this.http.get<ApiResponse>('/api/dashboard/dashboard_ship_ins.php').subscribe({
      next: (response) => {
        this.data = response.region_instance_count.map(row => ({
          ...row,
          date: new Date().toLocaleDateString(),
          status: 'Active'
        }));

        this.topFiveCustomers = response.top_five_customers_per_region;
        this.missedAppointments = response.missed_appointment_analysis;
        this.filteredAppointments = []; // Initialize filteredAppointments to empty

        // Extract unique customers
        const customerNames = this.topFiveCustomers.map(customer => customer.customer_name);
        this.uniqueCustomers = Array.from(new Set(customerNames));
        this.uniqueTimelineCustomers = Array.from(new Set(this.missedAppointments.map(appointment => appointment.customer_name)));
        this.shipperRegions = Array.from(new Set(this.data.map(item => item.shipper_region)));
        this.consigneeRegions = Array.from(new Set(this.data.map(item => item.consignee_region)));
        this.loading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load dashboard data.';
        console.error('Error fetching dashboard data:', error);
        this.loading = false;
      }
    });
  }

  fetchsmallDashboardData(): void {
    this.http.get<ApiResponse>('/api/dashboard/dashboard_ship_ins.php').subscribe({
      next: (response) => {
        this.rowData = response.expedited_and_high_priority_counts;
        this.loading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load dashboard data.';
        console.error('Error fetching dashboard data:', error);
        this.loading = false;
      }
    });
  }

  initInstanceCountChart(): void {
    const ctx = document.getElementById('instanceCountChart') as HTMLCanvasElement;
    this.instanceCountChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.shipperRegions,
        datasets: [{
          label: 'Instance Count by Region',
          data: this.shipperRegions.map(region =>
            this.data.filter(item => item.shipper_region === region).reduce((sum, item) => sum + item.instance_count, 0)
          ),
          backgroundColor: this.shipperRegions.map(() => this.getRandomColor()),
        }]
      },
      options: {
        plugins: {
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const region = tooltipItem.label;
                const count = tooltipItem.raw;
                return `${region}: ${count}`;
              }
            }
          }
        }
      }
    });
  }

  getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  updateInstanceCountChart(): void {
    if (this.instanceCountChart) {
      const instanceCounts = this.shipperRegions.map(region =>
        this.data.filter(item => item.shipper_region === region).reduce((sum, item) => sum + item.instance_count, 0)
      );
      this.instanceCountChart.data.labels = this.shipperRegions;
      this.instanceCountChart.data.datasets[0].data = instanceCounts;
      this.instanceCountChart.data.datasets[0].backgroundColor = this.shipperRegions.map(() => this.getRandomColor());
      this.instanceCountChart.update();
    }
  }

  onRegionChange(): void {
    if (this.selectedShipperRegion) {
      const filteredData = this.data.filter(item => item.shipper_region === this.selectedShipperRegion);
      this.consigneeRegions = Array.from(new Set(filteredData.map(item => item.consignee_region)));
      this.selectedConsigneeRegion = ''; // Reset selected consignee region
      this.updateInstanceCountChartWithSelectedShipper(filteredData);
    } else {
      this.consigneeRegions = []; // Clear consignee regions if no shipper region is selected
      this.updateInstanceCountChart();
    }
  }

  onCustomerChange(): void {
    // Update filteredCustomerData and shippers based on selected customer
    this.filteredCustomerData = this.topFiveCustomers.filter(customer => customer.customer_name === this.selectedCustomer);
    this.selectedCustomerShippers = Array.from(new Set(this.filteredCustomerData.map(customer => customer.shipper_region)));
    this.selectedShipper = ''; // Reset selected shipper
  }

  onShipperChange(): void {
    // Update the customer chart when a shipper is selected
    this.initCustomerChart();
  }
  initCustomerChart(): void {
    const ctx = document.getElementById('customerChart') as HTMLCanvasElement;
    if (this.customerChart) {
      this.customerChart.destroy(); // Destroy the existing chart before creating a new one
    }
    const selectedShipperData = this.filteredCustomerData.filter(customer => customer.shipper_region === this.selectedShipper);
    this.customerChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: selectedShipperData.map(customer => customer.consignee_region),
        datasets: [{
          label: 'Instance Count',
          data: selectedShipperData.map(customer => customer.instance_count),
          backgroundColor: selectedShipperData.map(() => this.getRandomColor()),
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });
  }

  updateInstanceCountChartWithSelectedShipper(filteredData: any[]): void {
    if (this.instanceCountChart) {
      const instanceCounts = this.consigneeRegions.map(region =>
        filteredData.filter(item => item.consignee_region === region).reduce((sum, item) => sum + item.instance_count, 0)
      );
      this.instanceCountChart.data.labels = this.consigneeRegions;
      this.instanceCountChart.data.datasets[0].data = instanceCounts;
      this.instanceCountChart.data.datasets[0].backgroundColor = this.consigneeRegions.map(() => this.getRandomColor());
      this.instanceCountChart.update();
    }
  }

  onTimelineFilterChange(): void {
    const filteredAppointments = this.missedAppointments.filter(appointment => {
      return (!this.selectedTimelineCustomer || appointment.customer_name === this.selectedTimelineCustomer) &&
             (!this.selectedTimelineDate || appointment.appointment_start.startsWith(this.selectedTimelineDate));
    });

    this.filteredAppointments = filteredAppointments;
    this.renderTimeline();
  }
  renderTimeline(): void {
    const timelineContainer = document.getElementById('timeline');
    if (timelineContainer) {
      // Clear the timeline container
      timelineContainer.innerHTML = '';

      this.filteredAppointments.forEach((appointment, index) => {
        // Create a timeline item for each missed appointment
        const timelineItem = document.createElement('div');
        timelineItem.className = 'timeline-card';
        timelineItem.style.left = `${index * 120}px`; // Adjust positioning logic as needed

        // const timelineDot = document.createElement('div');
        // timelineDot.className = 'timeline-dot';

        // const title = document.createElement('div');
        // title.className = 'title';
        // title.innerText = appointment.customer_name;

        const date = document.createElement('div');
        date.className = 'date';
        date.innerText = new Date(appointment.appointment_start).toLocaleString();

        // const alternateReference = document.createElement('div');
        // alternateReference.className = 'alternate-reference';
        // alternateReference.innerText = `Reference: ${appointment.alternate_reference}`;

        const status = document.createElement('div');
        status.className = `status ${appointment.departure_status === 'Early Departure' ? 'early' : 'late'}`;
        status.innerText = appointment.departure_status;

        const carrier = document.createElement('div');
        carrier.className = 'carrier';
        carrier.innerText = `Carrier: ${appointment.carrier_name}`;

        const additionalInfo = document.createElement('div');
        additionalInfo.className = 'additional-info';
        additionalInfo.innerHTML = `
          // <div>Appointment End: ${new Date(appointment.appointment_end).toLocaleString()}</div>
          <div>Load Start: ${new Date(appointment.load_start).toLocaleString()}</div>
          // <div>Load End: ${new Date(appointment.load_end).toLocaleString()}</div>
          <div>Arrival Status: ${appointment.arrival_status}</div>
        `;

        // timelineItem.appendChild(timelineDot);
        // timelineItem.appendChild(title);
        timelineItem.appendChild(date);
        // timelineItem.appendChild(alternateReference);
        timelineItem.appendChild(status);
        timelineItem.appendChild(carrier);
        timelineItem.appendChild(additionalInfo);

        timelineContainer.appendChild(timelineItem);
      });
    }
  }
}
